import { z } from 'zod';
import type { SsnError } from '../validation/ssn.js';
import type { PatientSummaryDto } from './patients.js';

/**
 * Import de patients depuis un fichier CSV : un cabinet qui arrive d'un autre logiciel, ou d'un
 * tableur, retrouve ses dossiers administratifs sans les ressaisir.
 *
 * **Le navigateur lit le fichier, le serveur décide.** L'écran découpe le CSV pour montrer ses
 * colonnes et les faire rattacher aux champs du dossier ; il n'envoie ensuite **que les colonnes
 * retenues** — un export d'un autre logiciel porte souvent des colonnes cliniques qui n'ont rien à
 * faire ici, et ce qui n'est pas importé ne voyage pas. Le serveur convertit chaque cellule, valide
 * la ligne avec `patientCreateSchema`, cherche les doublons et crée par le service de création :
 * aucune règle n'est tenue par le navigateur.
 *
 * Deux temps, une seule route : l'**essai** (`dry_run`) n'écrit rien et nourrit l'aperçu,
 * l'**exécution** revalide tout — le serveur ne croit pas l'aperçu sur parole — puis crée.
 */

/** Champs du dossier qu'une colonne peut alimenter. Les autres se saisissent dans le dossier. */
export const patientImportFields = [
  'lastName',
  'firstName',
  'birthDate',
  'sex',
  'email',
  'phone',
  'mobilePhone',
  'street',
  'houseNumber',
  'addressDetails',
  'postalCode',
  'city',
  'country',
  'ssn',
  'ssnCountry',
  'profession',
  'primaryPhysician',
  'notesAdmin',
] as const;
export type PatientImportField = (typeof patientImportFields)[number];

/** Ce que la création rapide exige déjà : sans eux, une colonne ne suffit pas à faire un dossier. */
export const patientImportRequiredFields = [
  'lastName',
  'firstName',
  'birthDate',
] as const satisfies readonly PatientImportField[];

/**
 * Bornes. Deux mille lignes couvrent la patientèle d'un cabinet individuel ; deux mégaoctets, un
 * tel fichier avec ses notes — en base 1000, comme l'écran les affiche. Le corps de la requête ne
 * porte que les colonnes retenues, en JSON : la marge couvre l'échappement.
 */
export const PATIENT_IMPORT_MAX_ROWS = 2000;
export const PATIENT_IMPORT_MAX_FILE_BYTES = 2_000_000;
export const PATIENT_IMPORT_MAX_BODY_BYTES = 4 * 1024 * 1024;
/**
 * Borne d'une cellule dans la requête. Au-delà du plus long champ du dossier — les notes, 5 000 —
 * c'est la validation de la ligne qui répond « trop long » ; ceci ne fait que borner le corps.
 */
export const PATIENT_IMPORT_MAX_CELL_LENGTH = 10_000;

export const patientImportModes = ['dry_run', 'execute'] as const;
export type PatientImportMode = (typeof patientImportModes)[number];

const importRowSchema = z
  .object({
    /** Numéro de la ligne dans le fichier, en-tête compris : celui que le tableur affiche. */
    line: z.int().min(2),
    /** Une cellule par champ de `fields`, dans le même ordre. Une cellule absente vaut vide. */
    cells: z.array(z.string().max(PATIENT_IMPORT_MAX_CELL_LENGTH)).max(patientImportFields.length),
  })
  .strict();

export const patientImportRequestSchema = z
  .object({
    mode: z.enum(patientImportModes),
    fields: z.array(z.enum(patientImportFields)).min(1).max(patientImportFields.length),
    rows: z.array(importRowSchema).min(1).max(PATIENT_IMPORT_MAX_ROWS),
    /** Lignes à créer malgré un doublon probable. Par défaut, un doublon est ignoré. */
    createDuplicates: z.array(z.int().min(2)).max(PATIENT_IMPORT_MAX_ROWS).default([]),
  })
  .strict()
  .refine((input) => new Set(input.fields).size === input.fields.length, {
    error: 'Un champ ne se rattache qu’à une seule colonne',
    path: ['fields'],
  })
  .refine((input) => patientImportRequiredFields.every((field) => input.fields.includes(field)), {
    error: 'Nom, prénom et date de naissance doivent être rattachés',
    path: ['fields'],
  })
  .refine((input) => new Set(input.rows.map((row) => row.line)).size === input.rows.length, {
    error: 'Chaque ligne n’apparaît qu’une fois',
    path: ['rows'],
  });
export type PatientImportRequest = z.infer<typeof patientImportRequestSchema>;

/**
 * Ce qui cloche dans une ligne, en codes que l'écran traduit. `ssn_taken` : le numéro est déjà
 * porté par un dossier du cabinet que la détection de doublons ne propose pas — archivé ou à la
 * corbeille —, et l'unicité du numéro par cabinet interdit un second dossier.
 */
export const patientImportIssueCodes = [
  'required',
  'date_unreadable',
  'date_invalid',
  'date_out_of_range',
  'email_invalid',
  'sex_unreadable',
  'country_unreadable',
  'ssn_country_unreadable',
  'ssn_invalid',
  'ssn_taken',
  'too_long',
  'invalid',
] as const;
export type PatientImportIssueCode = (typeof patientImportIssueCodes)[number];

export interface PatientImportIssueDto {
  field: PatientImportField;
  code: PatientImportIssueCode;
  /** Pour `ssn_invalid` : le défaut, dans les codes de `validateSsn`. */
  ssnError?: SsnError;
  /** Pour `too_long` : la longueur admise. */
  max?: number;
}

/**
 * `ready`, `duplicate` et `error` pour l'essai ; `created`, `skipped` (doublon laissé de côté),
 * `error` et `over_quota` pour l'exécution.
 */
export const patientImportStatuses = [
  'ready',
  'duplicate',
  'created',
  'skipped',
  'error',
  'over_quota',
] as const;
export type PatientImportStatus = (typeof patientImportStatuses)[number];

export interface PatientImportRowDto {
  line: number;
  status: PatientImportStatus;
  issues: PatientImportIssueDto[];
  /** Dossiers existants **du cabinet** qui décrivent probablement la même personne. */
  candidates: PatientSummaryDto[];
  /** Ligne précédente du fichier qui décrit la même personne. */
  duplicateOfLine: number | null;
  /**
   * Faux quand le doublon porte le même numéro de sécurité sociale : un seul dossier du cabinet
   * peut le porter, « créer quand même » serait refusé.
   */
  forceable: boolean;
  /** Le dossier créé, à l'exécution. */
  patientId: string | null;
}

export interface PatientImportResultDto {
  mode: PatientImportMode;
  rows: PatientImportRowDto[];
  /** Quota de dossiers du cabinet **après** l'opération — inchangé par un essai. */
  quota: { limit: number; used: number };
}

/**
 * Les lignes que l'exécution tente de créer, dans l'ordre du fichier : les lignes prêtes, et les
 * doublons que le praticien a choisi de créer quand même, sauf ceux qu'un même numéro rend
 * impossibles. L'aperçu en tire ce qu'il annonce, l'exécution ce qu'elle fait : une seule règle.
 */
export function selectRowsToCreate<
  Row extends { line: number; status: PatientImportStatus; forceable: boolean },
>(rows: readonly Row[], createDuplicates: ReadonlySet<number>): Row[] {
  return rows
    .filter(
      (row) =>
        row.status === 'ready' ||
        (row.status === 'duplicate' && row.forceable && createDuplicates.has(row.line)),
    )
    .sort((a, b) => a.line - b.line);
}

export function countPatientImportRows(
  rows: readonly { status: PatientImportStatus }[],
): Record<PatientImportStatus, number> {
  const counts: Record<PatientImportStatus, number> = {
    ready: 0,
    duplicate: 0,
    error: 0,
    created: 0,
    skipped: 0,
    over_quota: 0,
  };
  for (const row of rows) counts[row.status] += 1;
  return counts;
}
